import { useState, useEffect, useCallback } from 'react';
import { featureFlagsApi } from '../services/api';

let _cache = null;
let _cacheTime = 0;
const CACHE_TTL = 120000; // 2 minutes

export function useFeatureFlags() {
  const [flags, setFlags] = useState(_cache);
  const [loading, setLoading] = useState(!_cache);

  const fetchFlags = useCallback(async () => {
    const now = Date.now();
    if (_cache && (now - _cacheTime) < CACHE_TTL) {
      setFlags(_cache);
      setLoading(false);
      return;
    }

    try {
      // 1) tenta endpoint /me (com auth) — pode falhar para anonymous
      let data;
      try {
        data = await featureFlagsApi.getMyFlags();
      } catch {
        data = { enabled_features: [], feature_paths: {}, role: 'public' };
      }
      // 2) sempre busca também os flags públicos (widgets visíveis a todos)
      try {
        const pub = await featureFlagsApi.getPublicFlags();
        data = {
          ...data,
          enabled_features: Array.from(new Set([...(data.enabled_features || []), ...(pub.enabled_features || [])])),
          feature_paths: { ...(data.feature_paths || {}), ...(pub.feature_paths || {}) },
        };
      } catch { /* ignore */ }
      _cache = data;
      _cacheTime = Date.now();
      setFlags(data);
    } catch {
      setFlags({ enabled_features: [], feature_paths: {}, role: 'user' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFlags(); }, [fetchFlags]);

  const isFeatureEnabled = useCallback((featureId) => {
    if (!flags || !flags.enabled_features) return true; // default allow
    return flags.enabled_features.includes(featureId);
  }, [flags]);

  const isPathEnabled = useCallback((path) => {
    if (!flags || !flags.feature_paths) return true; // default allow
    const featureId = flags.feature_paths[path];
    if (!featureId) return true; // path not gated
    return flags.enabled_features.includes(featureId);
  }, [flags]);

  const invalidateCache = useCallback(() => {
    _cache = null;
    _cacheTime = 0;
    fetchFlags();
  }, [fetchFlags]);

  return { flags, loading, isFeatureEnabled, isPathEnabled, invalidateCache };
}
